import React, { createContext, useCallback, useMemo, useRef, useState } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import ReactModal from 'react-modal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LightBoxContext from './LightBoxContext'
import { MODAL_TRANSITION_DURATION } from './modalConstants'
import { DrawerSlideDownAnimation, DrawerSlideUpAnimation } from './ModalSheet'
import noop from 'lib/function/noop'

export interface LightBoxElementContextValue<R> {
  open?: boolean
  resolve: NonNullable<R> extends never ? (result?: undefined) => void : (result: R) => void
  afterClose: () => void
}

export const LightBoxElementContext = createContext<LightBoxElementContextValue<undefined>>({
  open: false,
  resolve: noop,
  afterClose: noop,
})

const LightboxOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  backdrop-filter: blur(2px);
  max-height: calc(var(--vh, 1vh) * 100);
  overflow-y: unset !important; /* this is only here until the legacy modal is ditched */

  ${mediaQueryUp.tablet} {
    align-items: center;
    justify-content: center;
    && {
      transition-property: opacity, background-color;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const LightboxElement = styled(ReactModal)`
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: 0;
  box-shadow: ${props => props.theme.shadow.top.medium};
  min-height: ${rem(54)};
  height: 100%;
  width: 100%;

  animation-name: ${DrawerSlideDownAnimation};
  animation-iteration-count: 1;
  animation-duration: 300ms;
  animation-fill-mode: forwards;

  &.is-open {
    animation-name: ${DrawerSlideUpAnimation};
  }

  ${mediaQueryUp.tablet} {
    animation: none;

    &.is-open {
      animation: none;
    }
  }
`

const CloseButton = styled(IconButton)`
  position: absolute;
  right: ${rem(16)};
  top: ${rem(16)};
  z-index: 1;
`

function LightBoxProvider(props: React.PropsWithChildren) {
  const [lightbox, setLightbox] = useState<{
    resolve:(result?: unknown) => void
    element: React.ReactNode
  }>()
  const afterCloseRef = useRef<(() => unknown) | undefined>(undefined)
  const afterClosePromiseRef = useRef<Promise<void> | undefined>(undefined)
  const [open, setOpen] = useState<boolean>()

  const showLightbox = useCallback(async(lightbox: React.ReactNode) => {
    const lightboxResolve = (resolve: (result: unknown) => void) => {
      setLightbox({
        element: lightbox,
        resolve,
      })
      setOpen(true)
    }

    const afterResolve = (result: unknown) => {
      // start closing the modal, we still want the element rendered so it doesn't jank around
      setOpen(false)
      // we need to wait till it's closed before we can fully resolve
      // as that's when the modal is truly "done"
      const onAfterClose = new Promise<any>(resolve => {
        afterCloseRef.current = () => resolve(result)
      })
      afterClosePromiseRef.current = onAfterClose
      // chain the promise until after the close is done
      return onAfterClose
    }

    if (afterClosePromiseRef.current) {
      // an existing modal is already open and we're trying to show a new one...
      // wait for the old one to close then show the next one
      return afterClosePromiseRef.current.then(() => new Promise(lightboxResolve)).then(afterResolve)
    }
    return new Promise(lightboxResolve).then(afterResolve)
  }, [])

  const handleClose = useCallback((event) => {
    event.stopPropagation()
    lightbox?.resolve()
    setOpen(false)
  }, [lightbox])

  const lightBoxElementContextValue = useMemo<LightBoxElementContextValue<unknown>>(() => ({
    open,
    resolve: lightbox?.resolve ?? noop,
    afterClose: () => {
      // remove the modal element from being rendered, we're done with it
      setLightbox(undefined)
      // after close is done, finish the showModal call and resolve it back to the caller
      afterCloseRef.current?.()
      afterCloseRef.current = undefined
      afterClosePromiseRef.current = undefined
    },
  }), [lightbox, open])

  return <>
    <LightBoxElementContext.Provider value={lightBoxElementContextValue}>
      <LightboxElement
        className={cn({ 'is-open': open })}
        overlayElement={(props, content) => <LightboxOverlay {...props}>{content}</LightboxOverlay>}
        overlayClassName="luxkit-lightbox__overlay"
        closeTimeoutMS={MODAL_TRANSITION_DURATION}
        isOpen={open}
        shouldCloseOnEsc
        shouldReturnFocusAfterClose={false}
        onRequestClose={handleClose}
        onAfterClose={lightBoxElementContextValue.afterClose}
      >
        <CloseButton
          kind="tertiary"
          variant="ghost"
          outdent="right"
          onClick={handleClose}
        >
          <LineTimesIcon />
        </CloseButton>
        {lightbox?.element}
      </LightboxElement>
    </LightBoxElementContext.Provider>
    <LightBoxContext.Provider value={showLightbox}>
      {props.children}
    </LightBoxContext.Provider>
  </>
}

export default LightBoxProvider
